import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Modal, Box, Typography, InputBase, Fade } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useGlobalSearch, type SearchableProject } from './useGlobalSearch';
import { useAuth } from '../core/auth/useAuth';
import { colors } from '../core/theme/theme';

// ── Navigation page definitions ──────────────────────────────────────
interface NavPage {
  type: 'page';
  id: string;
  path: string;
  labelKey: string;
  Icon: SvgIconComponent;
  keywords: string[];
  adminOnly?: boolean;
}

const NAV_PAGES: NavPage[] = [
  { type: 'page', id: 'projects', path: '/projects', labelKey: 'nav.projectsHub', Icon: FolderOutlinedIcon, keywords: ['projects', 'hub', 'projets'] },
  { type: 'page', id: 'tracker', path: '/kronos/tracker', labelKey: 'nav.tracker', Icon: TimerOutlinedIcon, keywords: ['tracker', 'timer', 'time', 'kronos', 'chrono'] },
  { type: 'page', id: 'timesheet', path: '/kronos/timesheet', labelKey: 'nav.timesheet', Icon: ViewWeekOutlinedIcon, keywords: ['timesheet', 'week', 'feuille', 'temps', 'semaine'] },
  { type: 'page', id: 'calendar', path: '/kronos/calendar', labelKey: 'nav.calendar', Icon: CalendarMonthIcon, keywords: ['calendar', 'calendrier', 'agenda'] },
  { type: 'page', id: 'dashboard', path: '/kronos/dashboard', labelKey: 'nav.dashboard', Icon: DashboardOutlinedIcon, keywords: ['dashboard', 'tableau', 'bord', 'analytics', 'analyse'] },
  { type: 'page', id: 'report', path: '/kronos/report', labelKey: 'nav.report', Icon: AssessmentOutlinedIcon, keywords: ['report', 'rapport', 'export'] },
  { type: 'page', id: 'clients', path: '/customers', labelKey: 'nav.customers', Icon: BusinessOutlinedIcon, keywords: ['clients', 'client', 'company', 'entreprise'] },
  { type: 'page', id: 'settings', path: '/settings', labelKey: 'nav.settings', Icon: SettingsOutlinedIcon, keywords: ['settings', 'admin', 'parametres', 'config'], adminOnly: true },
  { type: 'page', id: 'notifications', path: '/notifications', labelKey: 'nav.notifications', Icon: NotificationsOutlinedIcon, keywords: ['notifications', 'alerts', 'inbox'] },
];

// ── Quick actions (create forms) ────────────────────────────────────
interface QuickAction {
  type: 'action';
  id: string;
  path: string;
  labelKey: string;
  Icon: SvgIconComponent;
  keywords: string[];
  adminOnly?: boolean;
}

const QUICK_ACTIONS: QuickAction[] = [
  { type: 'action', id: 'new-project', path: '/projects?new=1', labelKey: 'search.newProject', Icon: AddCircleOutlineIcon, keywords: ['new', 'create', 'project', 'nouveau', 'créer', 'projet'], adminOnly: true },
  { type: 'action', id: 'new-customer', path: '/customers?new=1', labelKey: 'search.newCustomer', Icon: AddCircleOutlineIcon, keywords: ['new', 'create', 'customer', 'client', 'nouveau', 'créer'] },
];

// ── Unified result type ──────────────────────────────────────────────
type PaletteItem =
  | { type: 'page'; page: NavPage }
  | { type: 'action'; action: QuickAction }
  | { type: 'project'; project: SearchableProject };

interface Props {
  open: boolean;
  onClose: () => void;
  fullScreen?: boolean;
}

export default function CommandPalette({ open, onClose, fullScreen = false }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { options } = useGlobalSearch();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.roles.includes('admin') ?? false;

  const visiblePages = useMemo(
    () => NAV_PAGES.filter((p) => !p.adminOnly || isAdmin),
    [isAdmin],
  );

  const visibleActions = useMemo(
    () => QUICK_ACTIONS.filter((a) => !a.adminOnly || isAdmin),
    [isAdmin],
  );

  // Reset and focus on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // ── Build mixed results ────────────────────────────────────────────
  const { pages, actions, projects, flat } = useMemo(() => {
    const q = query.trim().toLowerCase();

    // Filter pages
    const filteredPages: NavPage[] = q
      ? visiblePages.filter((p) => {
          const label = t(p.labelKey).toLowerCase();
          return (
            label.includes(q) ||
            p.keywords.some((kw) => kw.includes(q))
          );
        })
      : visiblePages; // show all pages when empty

    // Filter actions
    const filteredActions: QuickAction[] = q
      ? visibleActions.filter((a) => {
          const label = t(a.labelKey).toLowerCase();
          return (
            label.includes(q) ||
            a.keywords.some((kw) => kw.includes(q))
          );
        })
      : visibleActions; // show all actions when empty

    // Filter projects
    const filteredProjects: SearchableProject[] = q
      ? options
          .filter(
            (p) =>
              p.code.toLowerCase().includes(q) ||
              p.name.toLowerCase().includes(q) ||
              p.clientName.toLowerCase().includes(q),
          )
          .sort((a, b) => {
            const aCode = a.code.toLowerCase();
            const bCode = b.code.toLowerCase();
            if (aCode === q && bCode !== q) return -1;
            if (bCode === q && aCode !== q) return 1;
            if (aCode.startsWith(q) && !bCode.startsWith(q)) return -1;
            if (bCode.startsWith(q) && !aCode.startsWith(q)) return 1;
            return a.name.localeCompare(b.name);
          })
      : [];

    // Flat list for keyboard nav
    const flatList: PaletteItem[] = [
      ...filteredPages.map((p): PaletteItem => ({ type: 'page', page: p })),
      ...filteredActions.map((a): PaletteItem => ({ type: 'action', action: a })),
      ...filteredProjects.map((p): PaletteItem => ({ type: 'project', project: p })),
    ];

    return { pages: filteredPages, actions: filteredActions, projects: filteredProjects, flat: flatList };
  }, [query, options, visiblePages, visibleActions, t]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [flat]);

  // Scroll active item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const active = list.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const selectItem = useCallback(
    (item: PaletteItem) => {
      if (item.type === 'page') {
        navigate(item.page.path);
      } else if (item.type === 'action') {
        navigate(item.action.path);
      } else {
        navigate(`/projects/${item.project.id}`);
      }
      onClose();
    },
    [navigate, onClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          if (flat[activeIndex]) {
            e.preventDefault();
            selectItem(flat[activeIndex]);
          }
          break;
      }
    },
    [flat, activeIndex, selectItem],
  );

  // Track the flat index so we can assign it across both sections
  let flatIndex = 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(2px)',
          },
        },
      }}
    >
      <Fade in={open} timeout={120}>
        <Box
          data-testid="command-palette-panel"
          data-fullscreen={fullScreen}
          sx={{
            position: 'absolute',
            top: fullScreen ? 0 : '18%',
            left: 0,
            right: 0,
            ...(fullScreen
              ? {
                  bottom: 0,
                  width: '100vw',
                  maxHeight: '100dvh',
                }
              : {
                  mx: 'auto',
                  width: { xs: '92vw', sm: 620 },
                  maxHeight: '64vh',
                }),
            bgcolor: colors.darkBg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            outline: 'none',
            border: fullScreen ? 'none' : `1px solid rgba(255,255,255,0.08)`,
          }}
        >
          {/* ── Search input row ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2.5,
              py: 0,
              gap: 1.5,
              borderBottom: `1px solid rgba(255,255,255,0.08)`,
            }}
          >
            <Typography sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>🥷</Typography>
            <InputBase
              inputRef={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('search.hattoriPlaceholder')}
              fullWidth
              sx={{
                py: 2,
                fontSize: 17,
                fontWeight: 500,
                color: '#fff',
                '& input::placeholder': {
                  color: 'rgba(255,255,255,0.3)',
                  opacity: 1,
                },
              }}
            />
            {fullScreen && (
              <IconButton
                aria-label={t('common.close')}
                onClick={onClose}
                sx={{ borderRadius: 0, color: 'rgba(255,255,255,0.7)' }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>

          {/* ── Results list ── */}
          {flat.length > 0 && (
            <Box ref={listRef} sx={{ overflowY: 'auto', flex: 1 }}>
              {/* Pages section */}
              {pages.length > 0 && (
                <>
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      px: 2.5,
                      pt: 1.5,
                      pb: 0.5,
                      color: 'rgba(255,255,255,0.35)',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                    }}
                  >
                    {t('search.sectionPages')}
                  </Typography>
                  {pages.map((page) => {
                    const idx = flatIndex++;
                    const Icon = page.Icon;
                    return (
                      <Box
                        key={page.id}
                        data-active={idx === activeIndex}
                        onClick={() => selectItem({ type: 'page', page })}
                        onMouseEnter={() => setActiveIndex(idx)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2.5,
                          py: 1.2,
                          cursor: 'pointer',
                          borderLeft: idx === activeIndex
                            ? `3px solid ${colors.red}`
                            : '3px solid transparent',
                          bgcolor: idx === activeIndex
                            ? 'rgba(255,89,74,0.12)'
                            : 'transparent',
                          transition: 'background-color 0.08s',
                        }}
                      >
                        <Icon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />
                        <Typography
                          variant="body2"
                          sx={{ flex: 1, color: '#fff', fontWeight: 500 }}
                        >
                          {t(page.labelKey)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 12 }}
                        >
                          {page.path}
                        </Typography>
                      </Box>
                    );
                  })}
                </>
              )}

              {/* Actions section */}
              {actions.length > 0 && (
                <>
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      px: 2.5,
                      pt: 1.5,
                      pb: 0.5,
                      color: 'rgba(255,255,255,0.35)',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                    }}
                  >
                    {t('search.sectionActions')}
                  </Typography>
                  {actions.map((action) => {
                    const idx = flatIndex++;
                    const Icon = action.Icon;
                    return (
                      <Box
                        key={action.id}
                        data-active={idx === activeIndex}
                        onClick={() => selectItem({ type: 'action', action })}
                        onMouseEnter={() => setActiveIndex(idx)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2.5,
                          py: 1.2,
                          cursor: 'pointer',
                          borderLeft: idx === activeIndex
                            ? `3px solid ${colors.red}`
                            : '3px solid transparent',
                          bgcolor: idx === activeIndex
                            ? 'rgba(255,89,74,0.12)'
                            : 'transparent',
                          transition: 'background-color 0.08s',
                        }}
                      >
                        <Icon sx={{ color: colors.yellow, fontSize: 20 }} />
                        <Typography
                          variant="body2"
                          sx={{ flex: 1, color: '#fff', fontWeight: 500 }}
                        >
                          {t(action.labelKey)}
                        </Typography>
                      </Box>
                    );
                  })}
                </>
              )}

              {/* Projects section */}
              {projects.length > 0 && (
                <>
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      px: 2.5,
                      pt: 1.5,
                      pb: 0.5,
                      color: 'rgba(255,255,255,0.35)',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                    }}
                  >
                    {t('search.sectionProjects')}
                  </Typography>
                  {projects.map((item) => {
                    const idx = flatIndex++;
                    return (
                      <Box
                        key={item.id}
                        data-active={idx === activeIndex}
                        onClick={() => selectItem({ type: 'project', project: item })}
                        onMouseEnter={() => setActiveIndex(idx)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2.5,
                          py: 1.2,
                          cursor: 'pointer',
                          borderLeft: idx === activeIndex
                            ? `3px solid ${colors.red}`
                            : '3px solid transparent',
                          bgcolor: idx === activeIndex
                            ? 'rgba(255,89,74,0.12)'
                            : 'transparent',
                          transition: 'background-color 0.08s',
                        }}
                      >
                        <FolderOutlinedIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 20 }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: colors.yellow,
                            minWidth: 60,
                            flexShrink: 0,
                            letterSpacing: '0.03em',
                          }}
                        >
                          {item.code}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            color: '#fff',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255,255,255,0.35)',
                            flexShrink: 0,
                            maxWidth: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.clientName}
                        </Typography>
                      </Box>
                    );
                  })}
                </>
              )}
            </Box>
          )}

          {/* ── Empty state ── */}
          {query.trim() !== '' && flat.length === 0 && (
            <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>
                {t('search.noResults')}
              </Typography>
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
}
